import styles from '@/styles/layout/Sidebar.module.scss';
import { useLogoutMutation } from '@features/auth/authApi';
import { logout } from '@features/auth/authSlice';
import { useSidebar } from '@hooks/useSidebar';
import { useAppDispatch } from '@store/hooks';
import { cn } from '@utils/cn';
import { BarChart2, Bell, ClipboardList, CreditCard, Flag, Layers, LayoutDashboard, LayoutList, LogOut, Newspaper, ShieldCheck, Users, Zap } from 'lucide-react';
import { memo, useCallback } from 'react';
import toast from 'react-hot-toast';
import { NavLink, useNavigate } from 'react-router-dom';

interface NavItem {
  name: string;
  path: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    icon: <LayoutDashboard className="h-5 w-5" />,
  },
  {
    name: 'Users',
    path: '/users',
    icon: <Users className="h-5 w-5" />,
  },
  {
    name: 'Categories',
    path: '/categories',
    icon: <LayoutList className="h-5 w-5" />,
  },
  {
    name: 'Groups',
    path: '/groups',
    icon: <Layers className="h-5 w-5" />,
  },
  {
    name: 'Reports',
    path: '/reports',
    icon: <Flag className="h-5 w-5" />,
  },
  {
    name: 'Polls',
    path: '/polls',
    icon: <BarChart2 className="h-5 w-5" />,
  },
  {
    name: 'Surveys',
    path: '/surveys',
    icon: <ClipboardList className="h-5 w-5" />,
  },
  {
    name: 'News',
    path: '/news',
    icon: <Newspaper className="h-5 w-5" />,
  },
  {
    name: 'Announcements',
    path: '/announcements',
    icon: <Bell className="h-5 w-5" />,
  },
  {
    name: 'Plan Features',
    path: '/plan-features',
    icon: <Zap className="h-5 w-5" />,
  },
  {
    name: 'Plans',
    path: '/plans',
    icon: <CreditCard className="h-5 w-5" />,
  },
  {
    name: 'Roles & Permissions',
    path: '/roles',
    icon: <ShieldCheck className="h-5 w-5" />,
  },
];

const Sidebar = memo(() => {
  const { isExpanded, setIsExpanded } = useSidebar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [logoutApi] = useLogoutMutation();

  const handleLogout = useCallback(() => {
    void logoutApi().finally(() => {
      dispatch(logout());
      toast.success('Logged out successfully!');
      void navigate('/login');
    });
  }, [logoutApi, dispatch, navigate]);

  const handleMouseEnter = useCallback(() => setIsExpanded(true), [setIsExpanded]);
  const handleMouseLeave = useCallback(() => setIsExpanded(false), [setIsExpanded]);

  return (
    <aside
      className={styles.sidebar}
      style={{ width: isExpanded ? '256px' : '64px' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* Logo */}
      <div
        className={styles.logoArea}
        style={{
          justifyContent: isExpanded ? 'flex-start' : 'center',
          paddingLeft: isExpanded ? '24px' : '16px',
          paddingRight: isExpanded ? '24px' : '16px',
        }}
      >
        <div className={styles.logoIcon}>
          <span>A</span>
        </div>
        <h1
          className={styles.logoText}
          style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? 'auto' : 0 }}
        >
          Admin
        </h1>
      </div>

      {/* Navigation */}
      <nav
        className={styles.nav}
        style={{
          paddingLeft: isExpanded ? '12px' : '8px',
          paddingRight: isExpanded ? '12px' : '8px',
        }}
      >
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(styles.navItem, isActive ? styles.navItemActive : '')
            }
          >
            <span className={styles.navIcon}>{item.icon}</span>
            <span
              className={styles.navLabel}
              style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? 'auto' : 0 }}
            >
              {item.name}
            </span>
          </NavLink>
        ))}
      </nav>

      {/* Logout Button */}
      <div
        className={styles.footer}
        style={{
          paddingTop: '1rem',
          paddingBottom: '1rem',
          paddingLeft: isExpanded ? '12px' : '8px',
          paddingRight: isExpanded ? '12px' : '8px',
        }}
      >
        <button onClick={handleLogout} className={styles.logoutBtn}>
          <span className={styles.logoutIcon}>
            <LogOut className="h-5 w-5" />
          </span>
          <span
            className={styles.logoutLabel}
            style={{ opacity: isExpanded ? 1 : 0, width: isExpanded ? 'auto' : 0 }}
          >
            Logout
          </span>
        </button>
      </div>
    </aside>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
